import { useState, useMemo, useEffect } from 'react'
import { useLocalForageKey } from '../../local-storage/useLocalForageKey.hooks'
import {
  Role,
  type MessageInput,
  type AgentRequestInput,
  type ChatMessageInput,
  type FeedbackEnum,
} from '../../../__codegen__/api'
import { useDashMessages } from '../../../intl/intl.hooks'
import { useAiAgentConversationUpsertMutation } from '../../../api/ai-agent/useAiAgentConversationUpsertMutation.hook'
import { trackEvent } from '../../events/trackEvent'
import { useCurrentAccountDetails } from '../../../api/current-account/currentAccount.hooks'
import { usePartnerId, useRetailerId } from '../../routing/params.hooks'
import { type AiAgentContextValue } from './utils/types'

const AI_AGENT_CONVERSATION_KEY = 'aiAgentConversation'
const AI_AGENT_CONVERSATION_ID_KEY = 'aiAgentConversationId'

// Helper function to generate a GUID
const generateConversationId = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })

// Helper function to convert MessageInput to ChatMessageInput
const convertToChatMessageInput = (
  message: MessageInput & { feedback?: FeedbackEnum; comment?: string; timestamp?: string }
): ChatMessageInput => ({
  content: message.content || '',
  role: message.role,
  toolCallId: message.toolCallId,
  toolCalls: message.toolCalls,
  feedback: message.feedback,
  comment: message.comment,
  ...(message.timestamp && { timestamp: message.timestamp }),
})

export const useAiAgentConversation = (
  executeAgentRequest: AiAgentContextValue['executeAgentRequest'],
  clearSummary?: () => void
) => {
  const {
    data: messages,
    setData: setMessages,
    loading: messagesLoading,
  } = useLocalForageKey<
    (MessageInput & { feedback?: FeedbackEnum; comment?: string; timestamp?: string })[]
  >(AI_AGENT_CONVERSATION_KEY)

  const {
    data: conversationId,
    setData: setConversationId,
    loading: conversationIdLoading,
  } = useLocalForageKey<string>(AI_AGENT_CONVERSATION_ID_KEY)

  const [loading, setLoading] = useState(false) // Added loading state
  const [thinkingMessage, setThinkingMessage] = useState<
    (MessageInput & { timestamp?: string }) | null
  >(null) // Track thinking state

  // Get user metadata for tracking
  const { account } = useCurrentAccountDetails()
  const partnerId = usePartnerId({ throwOnMissing: false })
  const retailerId = useRetailerId({ throwOnMissing: false })

  const intlMessages = useDashMessages({
    errorGeneric: 'aiAgent.conversation.error.generic',
  })

  const { upsertConversation } = useAiAgentConversationUpsertMutation()

  // Initialize conversationId if it doesn't exist
  useEffect(() => {
    if (!conversationId && !conversationIdLoading) {
      setConversationId(generateConversationId())
    }
  }, [conversationId, conversationIdLoading, setConversationId])

  const displayMessages = useMemo(() => {
    const baseMessages = (messages ?? [])
      .map((msg, originalIndex) => ({
        role: msg.role,
        content: msg.content,
        feedback: msg.feedback,
        comment: msg.comment,
        timestamp: msg.timestamp,
        originalIndex,
      }))
      .filter(
        ({ role, content }) =>
          (role == Role.User || role == Role.Assistant) && content && content !== ''
      )

    // Add thinking message if it exists, has content, and we're loading
    if (thinkingMessage && thinkingMessage.content && thinkingMessage.content.trim() && loading) {
      return [
        ...baseMessages,
        {
          role: thinkingMessage.role,
          content: thinkingMessage.content,
          feedback: undefined,
          comment: undefined,
          timestamp: thinkingMessage.timestamp,
          originalIndex: baseMessages.length, // Use next available index
        },
      ]
    }

    return baseMessages
  }, [messages, thinkingMessage, loading])

  // Helper function to upsert messages to the backend
  const upsertMessages = async (
    messagesToUpsert: (MessageInput & {
      feedback?: FeedbackEnum
      comment?: string
      timestamp?: string
    })[]
  ) => {
    if (!conversationId) return

    try {
      // Convert MessageInput to ChatMessageInput for the mutation
      const chatMessages: ChatMessageInput[] = messagesToUpsert.map(convertToChatMessageInput)

      const success = await upsertConversation(conversationId, chatMessages)

      if (!success) {
        console.warn('Failed to upsert conversation messages')
      }
    } catch (error) {
      console.error('Error upserting messages:', error)
      // Don't throw here to avoid breaking the conversation flow
    }
  }

  // Function to update message feedback or comment
  const updateMessage = async (
    messageIndex: number,
    updates: { feedback?: FeedbackEnum; comment?: string }
  ) => {
    const currentMessages = messages ?? []

    if (messageIndex >= 0 && messageIndex < currentMessages.length) {
      const updatedMessages = [...currentMessages]
      const currentMessage = updatedMessages[messageIndex]

      // Handle feedback updates
      if (updates.feedback !== undefined) {
        const currentFeedback = currentMessage.feedback
        // Toggle behavior: if the same feedback is applied again, remove it
        const newFeedback = currentFeedback === updates.feedback ? undefined : updates.feedback
        updatedMessages[messageIndex] = {
          ...currentMessage,
          feedback: newFeedback,
        }

        // Track feedback update
        trackEvent({
          id: 'aiAgent.chatBar.updateMessageFeedback',
          description: 'User updated message feedback',
          data: {
            partnerId,
            retailerId,
            canonicalId: account?.canonicalId,
            isInternal: account?.isInternal,
            messageIndex,
            feedback: newFeedback,
            conversationId,
            messageCount: currentMessages.length,
          },
        })
      }

      // Handle comment updates
      if (updates.comment !== undefined) {
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          comment: updates.comment,
        }

        // Track comment update
        trackEvent({
          id: 'aiAgent.chatBar.updateMessageComment',
          description: 'User added comment to AI agent message',
          data: {
            partnerId,
            retailerId,
            canonicalId: account?.canonicalId,
            isInternal: account?.isInternal,
            messageIndex,
            commentLength: updates.comment.length,
            conversationId,
            messageCount: currentMessages.length,
          },
        })
      }

      setMessages(updatedMessages)

      // Upsert the updated conversation
      await upsertMessages(updatedMessages)
    }
  }

  // Convenience functions for backward compatibility
  const updateMessageFeedback = async (messageIndex: number, feedback: FeedbackEnum) => {
    await updateMessage(messageIndex, { feedback })
  }

  const updateMessageComment = async (messageIndex: number, comment: string) => {
    await updateMessage(messageIndex, { comment })
  }

  // Helper function to clean messages for API calls by removing UI-only fields
  const cleanMessagesForApi = (
    messages: (MessageInput & {
      feedback?: FeedbackEnum
      comment?: string
      timestamp?: string
      originalIndex?: number
    })[]
  ): MessageInput[] =>
    messages.map(({ role, content, toolCalls, toolCallId }) => ({
      role,
      content,
      ...(toolCalls && { toolCalls }),
      ...(toolCallId && { toolCallId }),
    }))

  const sendMessage = async (messageContent: string) => {
    setLoading(true)

    // Track message sent
    trackEvent({
      id: 'aiAgent.chatBar.sendMessage',
      description: 'User sent message to AI agent',
      data: {
        partnerId,
        retailerId,
        canonicalId: account?.canonicalId,
        isInternal: account?.isInternal,
        messageLength: messageContent.length,
        conversationId,
        messageCount: (messages ?? []).length,
      },
    })

    const currentMessages = messages ?? []
    const newMessages: (MessageInput & { feedback?: FeedbackEnum; timestamp?: string })[] = [
      ...currentMessages,
      {
        role: Role.User,
        content: messageContent,
        timestamp: new Date().toISOString(),
      },
    ]

    // Optimistically update UI with user message
    setMessages(newMessages)

    try {
      const uiContextForApi: AgentRequestInput['uiContext'] = {
        urlPath: window.location.pathname,
      }
      // Clean messages before sending to API to remove UI-only fields like originalIndex
      const cleanedMessages = cleanMessagesForApi(newMessages)
      const response = await executeAgentRequest(
        cleanedMessages,
        uiContextForApi,
        assistantInProgressMessage => {
          // Store the thinking message separately so it persists during loading
          // Only store if it has actual content
          if (assistantInProgressMessage.content && assistantInProgressMessage.content.trim()) {
            setThinkingMessage({
              ...assistantInProgressMessage,
              timestamp: thinkingMessage?.timestamp, // Preserve existing timestamp if any
            })
          }
          // Don't update the main messages array yet - let displayMessages handle showing it
        }
      )

      // Clear thinking message and update with final response
      setThinkingMessage(null)

      // Tag AI response messages with the exact time they were received
      const aiResponseTimestamp = new Date().toISOString()

      // Process messages to ensure proper timestamping:
      // - Preserve existing timestamps (user messages already have send timestamps)
      // - Add receive timestamp to new AI assistant messages
      const messagesWithTimestamps = response.messages.map((msg: MessageInput) => {
        // Check if this message already has a timestamp (from our local state)
        const existingMessage = newMessages.find(
          existing =>
            existing.role === msg.role && existing.content === msg.content && existing.timestamp
        )

        if (existingMessage?.timestamp) {
          // Keep the existing timestamp (preserves user message send time)
          return { ...msg, timestamp: existingMessage.timestamp }
        } else if (msg.role === Role.Assistant) {
          // New AI response - tag with receive timestamp
          return { ...msg, timestamp: aiResponseTimestamp }
        } else {
          // Fallback for any other messages without timestamps - don't guess
          return { ...msg, timestamp: undefined }
        }
      })

      setMessages(messagesWithTimestamps)

      // Upsert the complete conversation after successful response
      await upsertMessages(messagesWithTimestamps)
    } catch (error) {
      console.error('Error sending message:', error)

      const errorMessages = [
        ...newMessages,
        {
          role: Role.Assistant,
          content: intlMessages.errorGeneric,
          timestamp: new Date().toISOString(),
        },
      ]

      setThinkingMessage(null)
      setMessages(errorMessages)

      // Also upsert the error state
      await upsertMessages(errorMessages)
    } finally {
      setLoading(false)
    }
  }

  const clearConversation = () => {
    setMessages([])
    setThinkingMessage(null)
    // Generate a new conversation ID when clearing
    setConversationId(generateConversationId())
    clearSummary?.()
  }

  const loadConversationMessages = (
    newConversationId: string,
    newMessages: (MessageInput & {
      feedback?: FeedbackEnum
      comment?: string
      timestamp?: string
    })[]
  ) => {
    setConversationId(newConversationId)
    setMessages(newMessages)
    setThinkingMessage(null)
    clearSummary?.() // Clear summary so it can be set by the loaded conversation
  }

  return {
    messages: displayMessages,
    sendMessage,
    clearConversation,
    loadConversationMessages,
    updateMessage,
    updateMessageFeedback,
    updateMessageComment,
    conversationId,
    loading: loading || messagesLoading || conversationIdLoading,
  }
}
